import React from 'react';
import { Editor, Node, Path, Point, Range, Transforms } from 'slate';
import { isTag } from './utils/navigation';
import { TagElement } from './components/TagElement';
import { WithTagOptions } from './types';

export const withTag =
  (options: WithTagOptions = {}) =>
  (editor: Editor): Editor => {
    const { isInline, insertText, renderElement, normalizeNode, deleteBackward, deleteForward, deleteFragment } = editor;
    const { isPDF } = options;

    editor.isInline = (element) => (isTag(element) || (element as any).type === 'exclude' || (element as any).type === 'footnote' ? true : isInline(element));

    // Intercept deleteBackward to detect when it would empty a tag
    editor.deleteBackward = (unit) => {
      if (editor.selection && Range.isCollapsed(editor.selection)) {
        const tagEntry = Editor.nodes(editor, { at: editor.selection, match: isTag }).next().value;
        if (tagEntry) {
          const [tagNode] = tagEntry;
          const text = Node.string(tagNode);
          if (text.length === 1) {
            const onTagAboutToBeEmpty = (editor as any).__onTagAboutToBeEmpty;
            if (onTagAboutToBeEmpty) {
              onTagAboutToBeEmpty((tagNode as any).tagId);
              return;
            }
          }
          deleteBackward(unit);
          (editor as any).__onTagTextDeleted?.((tagNode as any).tagId);
          return;
        }
      }
      deleteBackward(unit);
    };

    // Intercept deleteForward to detect when it would empty a tag
    editor.deleteForward = (unit) => {
      if (editor.selection && Range.isCollapsed(editor.selection)) {
        const tagEntry = Editor.nodes(editor, { at: editor.selection, match: isTag }).next().value;
        if (tagEntry) {
          const [tagNode] = tagEntry;
          const text = Node.string(tagNode);
          if (text.length === 1) {
            const onTagAboutToBeEmpty = (editor as any).__onTagAboutToBeEmpty;
            if (onTagAboutToBeEmpty) {
              onTagAboutToBeEmpty((tagNode as any).tagId);
              return;
            }
          }
          deleteForward(unit);
          (editor as any).__onTagTextDeleted?.((tagNode as any).tagId);
          return;
        }
      }
      deleteForward(unit);
    };

    // Intercept deleteFragment to detect when selection overlaps a tag
    editor.deleteFragment = (...args) => {
      if (editor.selection && !Range.isCollapsed(editor.selection)) {
        const tagEntry = Editor.nodes(editor, { at: editor.selection, match: isTag }).next().value;
        if (tagEntry) {
          const [tagNode] = tagEntry;
          deleteFragment(...args);
          (editor as any).__onTagTextDeleted?.((tagNode as any).tagId);
          return;
        }
      }
      deleteFragment(...args);
    };

    editor.insertText = (text) => {
      const { selection } = editor;
      if (selection && Range.isCollapsed(selection)) {
        const [tagEntry] = Editor.nodes(editor, { match: isTag });
        if (tagEntry) {
          const [, tagPath] = tagEntry;
          const end = Editor.end(editor, tagPath);
          if (Point.equals(selection.anchor, end)) {
            const after = Editor.after(editor, tagPath);
            if (after) {
              Transforms.select(editor, after);
            }
          }
        }
      }
      insertText(text);
    };

    editor.normalizeNode = (entry) => {
      const [node, path] = entry;

      if (isTag(node)) {
        if (Node.string(node).trim().length === 0) {
          const predecessor = Array.from(
            Editor.nodes(editor, {
              at: [],
              match: (n) => isTag(n) && n.continuedAt === node.tagId,
            })
          )[0];

          if (predecessor) {
            Transforms.setNodes(editor, { continuedAt: node.continuedAt || undefined } as any, { at: predecessor[1] });
          }

          Transforms.removeNodes(editor, { at: path });
          return;
        }

        const nextPath = Path.next(path);
        try {
          const [nextNode] = Editor.node(editor, nextPath);
          if (isTag(nextNode) && nextNode.tagId === node.tagId) {
            Transforms.mergeNodes(editor, { at: nextPath });
            return;
          }
        } catch {
          console.warn('Error accessing next node during tag normalization');
        }
      }

      normalizeNode(entry);
    };

    (editor as any).renderElement = (props: any) => {
      if (isTag(props.element)) {
        return <TagElement {...props} isPDF={isPDF} />;
      }
      return (renderElement as any)(props);
    };

    return editor;
  };

export default withTag;
